import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type {
  AdvancedSecurityConfig,
  UpdateSecurityConfigRequest,
  BiometricVerificationRequest,
  BiometricVerificationResult,
  LivenessSession,
  LivenessChallengeResponse,
  GeolocationData,
  GeolocationValidationResult,
  BlockchainAuditRecord,
  VoiceprintRegistrationRequest,
  VoiceVerificationRequest,
  VoiceVerificationResult,
  VoicePhrase,
  SecurityAuditLog,
  SecurityAuditQuery,
  SignatureData,
  SignatureHash,
} from '../types/advanced-security';

// ============ Configuration ============

export function useSecurityConfig(tenantId?: string) {
  return useQuery({
    queryKey: ['security-config', tenantId],
    queryFn: async () => {
      const params = tenantId ? `?tenantId=${tenantId}` : '';
      const { data } = await api.get<AdvancedSecurityConfig>(`/advanced-security/config${params}`);
      return data;
    },
  });
}

export function useUpdateSecurityConfig() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ tenantId, config }: { tenantId?: string; config: UpdateSecurityConfigRequest }) => {
      const params = tenantId ? `?tenantId=${tenantId}` : '';
      const { data } = await api.put<AdvancedSecurityConfig>(`/advanced-security/config${params}`, config);
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['security-config', variables.tenantId] });
    },
  });
}

// ============ Biometric Verification ============

export function useBiometricVerification() {
  return useMutation({
    mutationFn: async (request: BiometricVerificationRequest) => {
      const { data } = await api.post<BiometricVerificationResult>('/advanced-security/biometric/verify', request);
      return data;
    },
  });
}

export function useSignatureHash() {
  return useMutation({
    mutationFn: async (signatureData: SignatureData) => {
      const { data } = await api.post<SignatureHash>('/advanced-security/signature/hash', signatureData);
      return data;
    },
  });
}

export function useRegisterSignature() {
  return useMutation({
    mutationFn: async ({ userId, signatureData }: { userId: string; signatureData: SignatureData }) => {
      const { data } = await api.post('/advanced-security/signature/register', { userId, signatureData });
      return data;
    },
  });
}

// ============ Liveness ============

export function useStartLivenessSession() {
  return useMutation({
    mutationFn: async ({ userId, operationType, operationValue }: { 
      userId: string; 
      operationType: string; 
      operationValue?: number 
    }) => {
      const { data } = await api.post<LivenessSession>('/advanced-security/liveness/start', {
        userId,
        operationType,
        operationValue,
      });
      return data;
    },
  });
}

export function useLivenessSession(sessionId?: string) {
  return useQuery({
    queryKey: ['liveness-session', sessionId],
    queryFn: async () => {
      const { data } = await api.get<LivenessSession>(`/advanced-security/liveness/session/${sessionId}`);
      return data;
    },
    enabled: !!sessionId,
    refetchInterval: 1000, // Poll every second during active session
  });
}

export function useSubmitLivenessChallenge() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sessionId, response }: { sessionId: string; response: LivenessChallengeResponse }) => {
      const { data } = await api.post<LivenessSession>(`/advanced-security/liveness/challenge/${sessionId}`, response);
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['liveness-session', data.id], data);
    },
  });
}

// ============ Geolocation ============

export function useValidateGeolocation() {
  return useMutation({
    mutationFn: async ({ userId, geolocation, operationType }: {
      userId: string;
      geolocation: GeolocationData;
      operationType: string;
    }) => {
      const { data } = await api.post<GeolocationValidationResult>('/advanced-security/geolocation/validate', {
        userId,
        geolocation,
        operationType,
      });
      return data;
    },
  });
}

export function useGeolocationHistory(userId?: string, limit = 10) {
  return useQuery({
    queryKey: ['geolocation-history', userId, limit],
    queryFn: async () => {
      const { data } = await api.get<GeolocationData[]>(`/advanced-security/geolocation/history`, {
        params: { userId, limit },
      });
      return data;
    },
    enabled: !!userId,
  });
}

// ============ Blockchain Audit ============

export function useRegisterBlockchainSignature() {
  return useMutation({
    mutationFn: async ({ signatureHash, metadata }: { signatureHash: string; metadata?: Record<string, unknown> }) => {
      const { data } = await api.post<BlockchainAuditRecord>('/advanced-security/blockchain/register', {
        signatureHash,
        metadata,
      });
      return data;
    },
  });
}

export function useVerifyBlockchainSignature(hash?: string) {
  return useQuery({
    queryKey: ['blockchain-verify', hash],
    queryFn: async () => {
      const { data } = await api.get<BlockchainAuditRecord>(`/advanced-security/blockchain/verify/${hash}`);
      return data;
    },
    enabled: !!hash,
  });
}

// ============ Voice Signature ============

export function useVoicePhrase(complexity: 'LOW' | 'MEDIUM' | 'HIGH' = 'MEDIUM') {
  return useQuery({
    queryKey: ['voice-phrase', complexity],
    queryFn: async () => {
      const { data } = await api.get<VoicePhrase>('/advanced-security/voice/phrase', {
        params: { complexity },
      });
      return data;
    },
    staleTime: 0, // Always get fresh phrase
  });
}

export function useRegisterVoiceprint() {
  return useMutation({
    mutationFn: async (request: VoiceprintRegistrationRequest) => {
      const { data } = await api.post('/advanced-security/voice/register', request);
      return data;
    },
  });
}

export function useVerifyVoice() {
  return useMutation({
    mutationFn: async (request: VoiceVerificationRequest) => {
      const { data } = await api.post<VoiceVerificationResult>('/advanced-security/voice/verify', request);
      return data;
    },
  });
}

export function useUserVoiceprint(userId?: string) {
  return useQuery({
    queryKey: ['voiceprint', userId],
    queryFn: async () => {
      const { data } = await api.get(`/advanced-security/voice/voiceprint/${userId}`);
      return data;
    },
    enabled: !!userId,
  });
}

// ============ Security Audit ============

export function useSecurityAuditLogs(query: SecurityAuditQuery) {
  return useQuery({
    queryKey: ['security-audit-logs', query],
    queryFn: async () => {
      const { data } = await api.get<{ logs: SecurityAuditLog[]; total: number }>('/advanced-security/audit/logs', {
        params: query,
      });
      return data;
    },
  });
}

export function useSecurityAuditStats(tenantId?: string) {
  return useQuery({
    queryKey: ['security-audit-stats', tenantId],
    queryFn: async () => {
      const params = tenantId ? `?tenantId=${tenantId}` : '';
      const { data } = await api.get<{
        totalEvents: number;
        successRate: number;
        eventsByType: Record<string, number>;
        recentFailures: number;
      }>(`/advanced-security/audit/stats${params}`);
      return data;
    },
  });
}

// ============ Combined Hook for Verification Flow ============

export function useSecurityVerification() {
  const biometricMutation = useBiometricVerification();
  const startLivenessMutation = useStartLivenessSession();
  const submitChallengeMutation = useSubmitLivenessChallenge();
  const validateGeolocationMutation = useValidateGeolocation();
  const verifyVoiceMutation = useVerifyVoice();
  
  return {
    verifyBiometric: biometricMutation.mutateAsync,
    startLiveness: startLivenessMutation.mutateAsync,
    submitChallenge: submitChallengeMutation.mutateAsync,
    validateGeolocation: validateGeolocationMutation.mutateAsync,
    verifyVoice: verifyVoiceMutation.mutateAsync,
    isLoading: 
      biometricMutation.isPending || 
      startLivenessMutation.isPending || 
      submitChallengeMutation.isPending ||
      validateGeolocationMutation.isPending ||
      verifyVoiceMutation.isPending,
  };
}
